import React, { useMemo, useRef, useState } from 'react';
import { CheckCircle2, ImageOff, Loader2, Pencil, Scissors, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ImageLightbox } from '@/components/ui/image-lightbox';
import { Tooltip } from '@/components/ui/tooltip';
import { Popover } from '@/components/ui/popover';
import { HoverTime, useFrameTime } from '@/components/ui/hover-time';
import { useT } from '@/contexts/I18nContext';
import {
  correctTableIncident,
  type DailyReportPhoto,
  type TableIncidentCorrection,
} from '@/api/dailyReports';
import { plural } from '../reportModel';

const CARD_TITLE = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

// ── Выкладка: one row per rack, four fixed moment-columns ──────────────────────
//
// The backend emits up to four captioned moments per rack; we pivot the flat
// list into a per-rack grid with column headers so the operator reads it like a
// contact sheet (no captions per cell). Fill-% lives on hover + in the
// fullscreen caption; the timestamp is always visible (crucial here).

interface LayoutCol {
  caption: string;
  /** Older stored reports used a different caption for this column. */
  legacy?: string;
  label: string;
  tip: string;
}

const LAYOUT_COLS: LayoutCol[] = [
  {
    caption: 'На открытии',
    label: '20м после открытия',
    tip: 'Состояние полки в первые ~20 минут после открытия.',
  },
  {
    caption: 'Средняя выкладка',
    legacy: 'Полная выкладка',
    label: 'средняя выкладка',
    tip: 'Типичная заполненность за день (медиана, 50%).',
  },
  {
    caption: 'Самая большая просадка',
    label: 'самая большая просадка',
    tip: 'Самый пустой момент за день — без первых 20 минут после открытия и последних 3 часов перед закрытием.',
  },
  {
    caption: 'Перед закрытием',
    label: 'перед закрытием',
    tip: 'За ~30 минут до закрытия (ближе к закрытию полки часто уже разбирают).',
  },
];

interface RackGroup {
  name: string;
  type?: string;
  byCaption: Record<string, DailyReportPhoto>;
}

function colPhoto(r: RackGroup, c: LayoutCol): DailyReportPhoto | undefined {
  return r.byCaption[c.caption] ?? (c.legacy ? r.byCaption[c.legacy] : undefined);
}

function MetricChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="pointer-events-none absolute right-1 top-1 z-10 rounded bg-black/65 px-1 py-0.5 text-[10px] font-medium leading-none text-white opacity-0 backdrop-blur-sm transition-opacity duration-150 group-hover:opacity-100">
      {children}
    </span>
  );
}

function EmptyCell() {
  // Fill the row height (set by the row's images) instead of imposing a fixed
  // aspect — otherwise short rows leave a gap and tall rows get stretched.
  return (
    <div className="flex h-full min-h-[3rem] items-center justify-center rounded bg-muted/40">
      <ImageOff className="size-4 text-muted-foreground/40" />
    </div>
  );
}

export function LayoutKadry({ photos }: { photos: DailyReportPhoto[] }) {
  const ft = useFrameTime();

  const racks = useMemo<RackGroup[]>(() => {
    const map = new Map<string, RackGroup>();
    for (const p of photos) {
      const key = p.rack_name ?? '—';
      let g = map.get(key);
      if (!g) {
        g = { name: key, type: p.rack_type, byCaption: {} };
        map.set(key, g);
      }
      g.byCaption[p.caption] = p;
    }
    return [...map.values()];
  }, [photos]);

  // Flat list (rack → column order) for the lightbox + per-image captions.
  const flat = useMemo(() => {
    const arr: { url: string; caption: string }[] = [];
    for (const r of racks) {
      const label = r.type && r.type !== r.name ? `${r.type} · ${r.name}` : r.name;
      for (const c of LAYOUT_COLS) {
        const p = colPhoto(r, c);
        if (p?.image_url) {
          arr.push({
            url: p.image_url,
            caption: `${label} · ${c.label}${p.fill_pct != null ? ` · ${Math.round(p.fill_pct)}%` : ''}`,
          });
        }
      }
    }
    return arr;
  }, [racks]);

  const [open, setOpen] = useState<number | null>(null);
  const idxOf = (url?: string | null) => (url ? flat.findIndex((f) => f.url === url) : -1);

  if (!photos.length) return null;

  return (
    <div className="space-y-3">
      {/* Column headers — hover (tap on mobile) for the moment's definition.
          Always a 4-col grid, matching the image grid below: on mobile the
          headers used to be `hidden`, leaving four unlabelled image columns
          with no way to tell which moment each was (owner-reported). */}
      <div className="grid grid-cols-4 gap-2">
        {LAYOUT_COLS.map((c) => (
          <Tooltip key={c.caption} content={c.tip} side="top">
            <div className="cursor-help truncate text-center text-[10px] uppercase tracking-wide text-muted-foreground underline decoration-dotted decoration-muted-foreground/40 underline-offset-2">
              {c.label}
            </div>
          </Tooltip>
        ))}
      </div>

      {racks.map((r) => (
        <div key={r.name} className="space-y-1">
          <div className="text-xs font-medium text-foreground">
            {r.type && r.type !== r.name ? `${r.type} · ${r.name}` : r.name}
          </div>
          <div className="grid grid-cols-4 gap-2">
            {LAYOUT_COLS.map((c) => {
              const p = colPhoto(r, c);
              if (!p?.image_url) return <EmptyCell key={c.caption} />;
              const time = ft(p.at);
              return (
                <button
                  key={c.caption}
                  type="button"
                  onClick={() => {
                    const i = idxOf(p.image_url);
                    if (i >= 0) setOpen(i);
                  }}
                  className="group relative flex h-full items-center justify-center overflow-hidden rounded bg-muted"
                >
                  <img
                    src={p.image_url}
                    alt=""
                    loading="lazy"
                    className="block max-h-56 w-full cursor-zoom-in object-contain"
                  />
                  {time && <HoverTime time={time} always corner="top-left" />}
                  {p.fill_pct != null && <MetricChip>{Math.round(p.fill_pct)}%</MetricChip>}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {open != null && (
        <ImageLightbox
          srcs={flat.map((f) => f.url)}
          index={open}
          alt=""
          captions={flat.map((f) => f.caption)}
          onClose={() => setOpen(null)}
          onNavigate={setOpen}
        />
      )}
    </div>
  );
}

// ── Очередь: up to four full-width frames (longest / 80-pct / typical / calm) ───

const QUEUE_META: Record<string, { short: string; tip: string }> = {
  'Самая длинная очередь': { short: 'Самая длинная', tip: 'Момент самой длинной очереди за день.' },
  'Самая длинная': { short: 'Самая длинная', tip: 'Момент самой длинной очереди за день.' },
  'Обычная очередь': {
    short: 'Обычная',
    tip: '50% времени людей в очереди было меньше, чем на фото (не учитывает время совсем без очереди).',
  },
  'Обычная': {
    short: 'Обычная',
    tip: '50% времени людей в очереди было меньше, чем на фото (не учитывает время совсем без очереди).',
  },
  'Спокойное время': {
    short: 'Спокойное время',
    tip: 'Один из самых спокойных моментов за день (среди моментов, когда очередь была).',
  },
  'Длиннее обычной': {
    short: 'Длиннее обычной',
    tip: '20% очередей за день были длиннее, чем на фото (учитываются только моменты, когда очередь была).',
  },
};

function queueMeta(caption: string): { short: string; tip: string } {
  return (
    QUEUE_META[caption] ?? {
      short: caption.replace(/\s*очередь$/i, ''),
      // The 80-percentile frame ships from the backend with a self-describing
      // caption; surface it verbatim as the tooltip too.
      tip: caption,
    }
  );
}

export function QueueKadry({
  photos,
  columns = 2,
}: {
  photos: DailyReportPhoto[];
  /** 2 → 2×2 grid (bigger frames, taller card), 4 → one row of four. The
   *  caller picks whichever balances the кадры columns' heights better. */
  columns?: 2 | 4;
}) {
  const ft = useFrameTime();
  const withImages = photos.filter((p) => p.image_url);
  const [open, setOpen] = useState<number | null>(null);

  if (!photos.length) return null;

  const captions = withImages.map((p) => {
    const m = queueMeta(p.caption);
    const t = ft(p.at);
    return `${m.short}${p.captured_occupancy != null ? ` · ${p.captured_occupancy} чел` : ''}${t ? ` · ${t}` : ''}`;
  });

  return (
    <div className={columns === 2 ? 'grid grid-cols-2 gap-2' : 'grid grid-cols-2 gap-2 sm:grid-cols-4'}>
      {photos.map((p, i) => {
        const m = queueMeta(p.caption);
        const time = ft(p.at);
        const li = p.image_url ? withImages.findIndex((w) => w.image_url === p.image_url) : -1;
        return (
          <div key={i} className="space-y-1">
            <div className="group relative flex items-center justify-center overflow-hidden rounded bg-muted">
              {p.image_url ? (
                <button type="button" onClick={() => li >= 0 && setOpen(li)} className="block w-full">
                  <img
                    src={p.image_url}
                    alt=""
                    loading="lazy"
                    className="block w-full max-h-56 cursor-zoom-in object-contain"
                  />
                </button>
              ) : (
                <div className="flex aspect-[4/3] w-full items-center justify-center">
                  <ImageOff className="size-4 text-muted-foreground/40" />
                </div>
              )}
              {time && <HoverTime time={time} always corner="top-left" />}
              {p.captured_occupancy != null && <MetricChip>{p.captured_occupancy} чел</MetricChip>}
            </div>
            {m.tip ? (
              <Tooltip content={m.tip} side="top">
                <div className="cursor-help text-center text-[11px] font-medium text-foreground underline decoration-dotted decoration-muted-foreground/50 underline-offset-2">
                  {m.short}
                </div>
              </Tooltip>
            ) : (
              <div className="text-center text-[11px] font-medium text-foreground">{m.short}</div>
            )}
          </div>
        );
      })}

      {open != null && (
        <ImageLightbox
          srcs={withImages.map((w) => w.image_url)}
          index={open}
          alt=""
          captions={captions}
          onClose={() => setOpen(null)}
          onNavigate={setOpen}
        />
      )}
    </div>
  );
}

// ── Столы card: single longest table (title + duration chip) or, for several
//    tables, a connected "story" — each table's first frame outlined + labelled
//    «убрали через N минут», the rest linked by a thin connector line. ──────────

export interface TableGroup {
  name: string;
  photos: DailyReportPhoto[];
  /** Minutes the table stayed dirty (derived from the frames' timestamps). */
  minutes: number | null;
  /** Exclusion key (#264) — both halves come off the group's photos, so
   *  they're undefined for reports stored before table_id/stretch_start
   *  shipped; the X button hides itself in that case (nothing to target). */
  tableId?: number;
  stretchStart?: string;
}

/** Span (minutes) of the CONTIGUOUS dirty stretch these frames belong to.
 *
 * The picked frames are meant to be evenly spread across one dirty episode, so
 * first→last ≈ the whole episode. But the frame picker occasionally tacks on a
 * frame from far outside the episode (a later re-detection of the same table),
 * which used to inflate the span wildly (e.g. zone 47 "убрали через 591 мин"
 * when the real stretch was ~14 min). We detect that: sort the timestamps, and
 * if one inter-frame gap is a clear outlier (much larger than the others), cut
 * there and keep the larger contiguous cluster before measuring the span. */
function contiguousSpanMinutes(times: number[]): number | null {
  const ts = times.filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
  if (ts.length < 2) return null;

  const gaps = ts.slice(1).map((t, i) => t - ts[i]);
  // Reference = median gap; an outlier is one that dwarfs the typical spacing.
  const sortedGaps = [...gaps].sort((a, b) => a - b);
  const median = sortedGaps[Math.floor(sortedGaps.length / 2)] || 0;
  const maxGap = Math.max(...gaps);
  const OUTLIER_FACTOR = 4;
  const OUTLIER_FLOOR_MS = 20 * 60 * 1000; // ignore tiny jitter; only real jumps

  let lo = 0;
  let hi = ts.length - 1;
  if (maxGap > OUTLIER_FLOOR_MS && maxGap > median * OUTLIER_FACTOR) {
    const cut = gaps.indexOf(maxGap); // boundary: cluster A = [0..cut], B = [cut+1..]
    const aLen = cut + 1;
    const bLen = ts.length - (cut + 1);
    // Keep the larger cluster (ties → the earlier one, where the episode began).
    if (bLen > aLen) lo = cut + 1;
    else hi = cut;
  }
  if (hi <= lo) return null;
  return Math.max(1, Math.round((ts[hi] - ts[lo]) / 60000));
}

export function groupByTable(photos: DailyReportPhoto[]): TableGroup[] {
  // New reports carry an explicit episode index + the episode's real span
  // (alert → first clean frame). Old stored reports have neither — group by
  // adjacent table_name and derive the span from the frames' timestamps.
  const groups: TableGroup[] = [];
  const byKey = new Map<string, TableGroup>();
  for (const p of photos) {
    const name = p.table_name ?? p.caption ?? '—';
    const key = p.episode != null ? `ep_${p.episode}` : `name_${name}`;
    let g = byKey.get(key);
    if (!g) {
      g = { name, photos: [], minutes: null };
      byKey.set(key, g);
      groups.push(g);
    }
    g.photos.push(p);
  }
  for (const g of groups) {
    const explicit = g.photos.find((p) => p.cleared_after_min != null)?.cleared_after_min;
    if (explicit != null) {
      g.minutes = explicit;
    } else {
      const ts = g.photos.map((p) => (p.at ? new Date(p.at).getTime() : NaN));
      g.minutes = contiguousSpanMinutes(ts);
    }
    // All photos in a group share one dirty stretch, so table_id/stretch_start
    // are the same on every one — take them off the first.
    g.tableId = g.photos.find((p) => p.table_id != null)?.table_id;
    g.stretchStart = g.photos.find((p) => p.stretch_start != null)?.stretch_start;
  }
  return groups;
}

function clearedAfterLabel(minutes: number): string {
  return `убрали через ${minutes} ${plural(minutes, 'минуту', 'минуты', 'минут')}`;
}

// One frame in the story chain. Equal-height cells so the connector lines join
// their centres cleanly; the starting frame is outlined. No text over the
// photo — the duration lives on the closing card instead (a corner chip used
// to cover the timestamp).
export function StoryFrame({
  photo,
  isStart,
  onOpen,
  action,
}: {
  photo: DailyReportPhoto;
  isStart: boolean;
  onOpen?: () => void;
  /** Edit mode: a small control pinned to the photo's top-right corner
   *  (per-photo correction, Alexey 21.10). */
  action?: React.ReactNode;
}) {
  const ft = useFrameTime();
  const time = ft(photo.at);
  return (
    <div
      className={
        'group relative flex h-24 shrink-0 items-center justify-center overflow-hidden rounded bg-muted sm:h-28 ' +
        (isStart ? 'ring-2 ring-primary ring-offset-1 ring-offset-background' : '')
      }
    >
      {photo.image_url ? (
        <button type="button" onClick={onOpen} className="block h-full">
          <img
            src={photo.image_url}
            alt=""
            loading="lazy"
            className="block h-full w-auto max-w-none cursor-zoom-in object-contain"
          />
          {time && <HoverTime time={time} always corner="bottom-left" />}
        </button>
      ) : (
        <div className="flex h-full w-16 items-center justify-center">
          <ImageOff className="size-4 text-muted-foreground/40" />
        </div>
      )}
      {action && <div className="absolute top-1 right-1">{action}</div>}
    </div>
  );
}

/** Same colour + thickness as the starting frame's ring, so the chain reads
 *  as one highlighted episode. */
export function StoryConnector() {
  return <span className="h-0.5 w-4 shrink-0 bg-primary sm:w-6" />;
}

/** The episode's closing card: photo-row height, just wide enough for
 *  «убрали через N минут». Rendered even for single-photo episodes so every
 *  episode ends with its real cleanup time. */
function StoryClosing({ minutes }: { minutes: number }) {
  return (
    <span className="flex h-24 max-w-28 shrink-0 items-center justify-center rounded border-2 border-primary bg-primary/5 px-2.5 text-center text-xs font-semibold leading-snug text-primary sm:h-28">
      {clearedAfterLabel(minutes)}
    </span>
  );
}

// Episodes flow like wrapped text: several tables can share a row (small gap
// inside an episode, bigger gap between episodes) so short episodes don't
// each burn a whole row of whitespace.
export function TableStory({
  groups,
  onOpen,
  indexOf,
  editable,
  excludingKey,
  onCorrect,
}: {
  groups: TableGroup[];
  onOpen: (i: number) => void;
  indexOf: (p: DailyReportPhoto) => number;
  /** #264: staff-only edit mode — shows a per-episode X. */
  editable?: boolean;
  /** Correction key currently in flight, so only ITS X spins/disables. */
  excludingKey?: string | null;
  /** `cut` = per-photo correction (Alexey 21.10): from this photo to the
   *  episode end, or this photo only. Absent = the whole episode. */
  onCorrect?: (group: TableGroup, state: TableIncidentCorrection, cut?: TableCut) => void;
}) {
  return (
    <div className="flex flex-wrap items-start gap-x-7 gap-y-4">
      {groups.map((g, gi) => {
        const key = g.tableId != null && g.stretchStart ? `${g.tableId}:${g.stretchStart}` : null;
        const removable = editable && key != null;
        const removing = key != null && key === excludingKey;
        return (
          <div key={`${g.name}-${gi}`} className="space-y-1">
            <div className="flex flex-wrap items-center gap-y-3">
              {g.photos.map((p, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <StoryConnector />}
                  <StoryFrame
                    photo={p}
                    isStart={i === 0}
                    onOpen={() => {
                      const li = indexOf(p);
                      if (li >= 0) onOpen(li);
                    }}
                    action={removable && p.at ? (
                      <CorrectIncidentButton
                        removing={removing}
                        perPhoto
                        // The first photo opens the episode: «from here» = the whole episode.
                        onPick={(state, scope) => onCorrect?.(
                          g, state,
                          scope === 'from_here' && i === 0 ? undefined : { scope: scope ?? 'only_this', fromAt: p.at! },
                        )}
                      />
                    ) : undefined}
                  />
                </React.Fragment>
              ))}
              {g.minutes != null && (
                <>
                  <StoryConnector />
                  <StoryClosing minutes={g.minutes} />
                </>
              )}
            </div>
            <div className="flex items-center gap-1">
              <div className="truncate text-[11px] font-medium text-foreground">{g.name}</div>
              {removable && (
                <CorrectIncidentButton
                  removing={removing}
                  onPick={(state) => onCorrect?.(g, state)}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Destructive X next to an episode's table-name label, shown only in the
 *  pencil-toggled edit mode (#264). Own component so the busy spinner only
 *  replaces THIS button's icon, not every X on the card.
 *
 *  2026-09-01 (Alexey follow-up): opens a 3-way state picker instead of a
 *  window.confirm — the picked state also corrects the underlying
 *  TableOccupancy rows (see correctTableIncident), not just the report card.
 *  Sized up from size-4/size-3 and given a permanent tinted background
 *  (rather than only-on-hover) — the old version was too easy to miss
 *  next to the table name. */
export type TableCut = { scope: 'from_here' | 'only_this'; fromAt: string };

type SurfaceField = 'personal_items_table' | 'native_objects_table' | 'food_dishes_table' | 'dirty_dishes_table';
const SURFACE_OPTIONS: { field: SurfaceField; label: string }[] = [
  { field: 'personal_items_table', label: 'report.day.correctPersonal' },
  { field: 'native_objects_table', label: 'report.day.correctIncidentNative' },
  { field: 'food_dishes_table', label: 'report.day.correctFood' },
  { field: 'dirty_dishes_table', label: 'report.day.correctDirty' },
];
const emptySurface = (): Record<SurfaceField, boolean> => ({
  personal_items_table: false, native_objects_table: false, food_dishes_table: false, dirty_dishes_table: false,
});

/** Same cascade as pooled annotation; the last four heads are independent. */
function CorrectIncidentButton({ removing, onPick, perPhoto = false }: {
  removing: boolean;
  onPick: (labels: TableIncidentCorrection, scope?: TableCut['scope']) => void;
  perPhoto?: boolean;
}) {
  const { t } = useT();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [occupied, setOccupied] = useState<boolean | null>(null);
  const [inFrame, setInFrame] = useState<boolean | null>(null);
  const [surface, setSurface] = useState(emptySurface);
  const [scope, setScope] = useState<TableCut['scope']>('only_this');
  const tip = perPhoto ? t('report.day.correctFromPhoto') : t('report.day.removeIncident');
  const ready = occupied === true || (occupied === false && inFrame !== null);
  const save = () => {
    if (!ready || occupied === null) return;
    const labels: TableIncidentCorrection = { occupied };
    if (!occupied && inFrame !== null) {
      labels.table_in_frame = inFrame;
      if (inFrame) Object.assign(labels, surface);
    }
    setOpen(false);
    onPick(labels, perPhoto ? scope : undefined);
  };
  return <>
    <Tooltip content={tip} side="top">
      <Button ref={triggerRef} type="button" variant="ghost" size="icon-sm"
        className={cn('size-5 shrink-0 text-destructive hover:text-destructive',
          perPhoto ? 'bg-background/85 hover:bg-background' : 'bg-destructive/10 hover:bg-destructive/20')}
        disabled={removing} aria-haspopup="dialog" aria-expanded={open} aria-label={tip}
        onClick={() => {
          if (!open) { setOccupied(null); setInFrame(null); setSurface(emptySurface()); setScope('only_this'); }
          setOpen(!open);
        }}>
        {removing ? <Loader2 className="size-3.5 animate-spin" />
          : perPhoto ? <Scissors className="size-3.5" /> : <X className="size-3.5" />}
      </Button>
    </Tooltip>
    <Popover open={open} onOpenChange={setOpen} triggerRef={triggerRef} side="bottom" align="start">
      <div className="w-80 max-w-[calc(100vw-2rem)] max-h-[calc(100dvh-2rem)] overflow-y-auto space-y-3 p-3" role="dialog" aria-label={tip}>
        {perPhoto ? <fieldset className="space-y-1 text-sm">
          <legend className="mb-1 font-medium">{t('report.day.correctScope')}</legend>
          {(['only_this', 'from_here'] as const).map(value => <label key={value} className="flex items-center gap-2">
            <input type="radio" checked={scope === value} onChange={() => setScope(value)} />
            {t(value === 'only_this' ? 'report.day.correctScopeOnlyThis' : 'report.day.correctScopeFromHere')}
          </label>)}
        </fieldset> : <p className="text-sm font-medium">{t('report.day.correctScopeEpisode')}</p>}
        <fieldset>
          <legend className="mb-1 text-sm font-medium">{t('report.day.correctPresence')}</legend>
          <div className="flex gap-2">{[true, false].map(value => <Button key={String(value)} size="sm"
            variant={occupied === value ? 'default' : 'outline'} aria-pressed={occupied === value}
            onClick={() => { setOccupied(value); setInFrame(null); setSurface(emptySurface()); }}>
            {t(value ? 'report.day.correctIncidentOccupied' : 'report.day.correctIncidentFree')}
          </Button>)}</div>
        </fieldset>
        {occupied === false && <fieldset>
          <legend className="mb-1 text-sm font-medium">{t('report.day.correctPosition')}</legend>
          <div className="flex gap-2">{[true, false].map(value => <Button key={String(value)} size="sm"
            variant={inFrame === value ? 'default' : 'outline'} aria-pressed={inFrame === value}
            onClick={() => { setInFrame(value); setSurface(emptySurface()); }}>
            {t(value ? 'report.day.correctInFrame' : 'report.day.correctIncidentShifted')}
          </Button>)}</div>
        </fieldset>}
        {occupied === false && inFrame === true && <fieldset className="space-y-1 text-sm">
          <legend className="mb-1 font-medium">{t('report.day.correctSurface')}</legend>
          {SURFACE_OPTIONS.map(({field, label}) => <label key={field} className="flex items-center gap-2">
            <input type="checkbox" checked={surface[field]}
              onChange={e => setSurface(prev => ({...prev, [field]: e.target.checked}))} />
            {t(label)}
          </label>)}
          <p className="pt-1 text-xs text-muted-foreground">{t('report.day.correctCleanHint')}</p>
        </fieldset>}
        <p className="text-xs text-muted-foreground">{t('report.day.correctTrainingHint')}</p>
        <div className="flex gap-2">
          <Button size="sm" disabled={!ready} onClick={save}>{t('common.save')}</Button>
          <Button size="sm" variant="outline" onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
        </div>
      </div>
    </Popover>
  </>;
}

/** The correct-table-incident call + its busy/error state, shared by the
 *  report's TablesCard and the «Столы» panel of /management/status (whose
 *  episodes come from several days' reports, hence `reportId` per call). */
export function useTableCorrection(onChanged?: () => void) {
  const { t } = useT();
  const [excludingKey, setExcludingKey] = useState<string | null>(null);
  const [excludeError, setExcludeError] = useState<string | null>(null);
  const correct = async (reportId: number, g: TableGroup, state: TableIncidentCorrection, cut?: TableCut) => {
    if (g.tableId == null || !g.stretchStart) return;
    setExcludingKey(`${g.tableId}:${g.stretchStart}`);
    setExcludeError(null);
    try {
      await correctTableIncident(reportId, g.tableId, g.stretchStart, state, cut);
      onChanged?.();
    } catch {
      setExcludeError(t('report.day.removeIncidentFailed'));
    } finally {
      setExcludingKey(null);
    }
  };
  return { excludingKey, excludeError, correct };
}

export function TablesCard({
  photos,
  configured = false,
  className,
  reportId,
  canManage = false,
  onChanged,
}: {
  photos: DailyReportPhoto[];
  /** Zone monitors tables → with no episodes the card stays, with a positive
   *  "no dirty tables" note, instead of disappearing. */
  configured?: boolean;
  className?: string;
  /** Report row `photos` came from — required to call the exclude endpoint
   *  (#264). Without it (or without `canManage`) the pencil never renders. */
  reportId?: number;
  /** daily_report.manage only — shows the pencil that reveals per-episode
   *  X buttons (#264). Matches the BE gate on the exclude endpoint, so no
   *  button is rendered that would just 403 on click. */
  canManage?: boolean;
  /** Fired after a successful exclude so the caller can SWR-revalidate the
   *  report — this card only re-renders once fresh `photos` flow back in. */
  onChanged?: () => void;
}) {
  const { t } = useT();
  const ft = useFrameTime();
  const [open, setOpen] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const { excludingKey, excludeError, correct } = useTableCorrection(onChanged);

  const groups = useMemo(() => groupByTable(photos), [photos]);
  const single = groups.length <= 1;

  const withImages = useMemo(() => photos.filter((p) => p.image_url), [photos]);
  const indexOf = (p: DailyReportPhoto) =>
    p.image_url ? withImages.findIndex((w) => w.image_url === p.image_url) : -1;
  const captions = withImages.map((p) => {
    const tl = ft(p.at);
    return `${p.table_name ?? p.caption ?? ''}${tl ? ` · ${tl}` : ''}`.trim();
  });

  const canEdit = canManage && reportId != null;

  const handleCorrect = (g: TableGroup, state: TableIncidentCorrection, cut?: TableCut) => {
    if (reportId != null) void correct(reportId, g, state, cut);
  };

  if (!photos.length && !configured) return null;

  return (
    <Card className={cn('flex flex-col', className)}>
      <CardHeader className="flex flex-row items-center justify-between gap-2 pt-4 pb-2">
        <CardTitle className={CARD_TITLE}>
          {photos.length && single
            ? t('summaries.tableLongestTitle')
            : t('summaries.tablesLongestTitle')}
        </CardTitle>
        {canEdit && photos.length > 0 && (
          <Tooltip content={t('report.day.editIncidents')} side="top">
            <Button
              type="button"
              variant={editing ? 'secondary' : 'ghost'}
              size="icon-sm"
              onClick={() => setEditing((v) => !v)}
              aria-pressed={editing}
              aria-label={t('report.day.editIncidents')}
            >
              <Pencil className="size-3.5" />
            </Button>
          </Tooltip>
        )}
      </CardHeader>
      <CardContent className={cn(!photos.length && 'flex flex-1 items-center justify-center')}>
        {photos.length ? (
          <>
            <TableStory
              groups={groups}
              onOpen={setOpen}
              indexOf={indexOf}
              editable={editing}
              excludingKey={excludingKey}
              onCorrect={handleCorrect}
            />
            {excludeError && <p className="mt-2 text-xs text-destructive">{excludeError}</p>}
          </>
        ) : (
          // Good news, not a system error: centered (both axes) with a green check.
          <p className="flex items-center justify-center gap-2 py-4 text-center text-sm text-muted-foreground">
            <CheckCircle2 className="size-4 shrink-0 text-emerald-500" aria-hidden />
            {t('report.day.noDirtyTables')}
          </p>
        )}
      </CardContent>

      {open != null && (
        <ImageLightbox
          srcs={withImages.map((w) => w.image_url)}
          index={open}
          alt=""
          captions={captions}
          onClose={() => setOpen(null)}
          onNavigate={setOpen}
        />
      )}
    </Card>
  );
}
